import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Megaphone, 
  LayoutTemplate, 
  Layers,
  TrendingUp,
  Mail,
  MousePointerClick,
  Users,
  Plus,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { templatesApi, mauticApi, sectorsApi } from '../lib/api';
import { useCredits } from '../context/CreditsContext';

const MetricCard = ({ title, value, icon: Icon, change, color }) => (
  <Card className="card-hover" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold mt-2 font-[Manrope]">{value}</p>
          {change && (
            <p className={`text-sm mt-1 flex items-center gap-1 ${change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              <TrendingUp className="w-3 h-3" />
              {change > 0 ? '+' : ''}{change}%
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuickAction = ({ title, description, icon: Icon, href, gradient }) => (
  <Link to={href} data-testid={`quick-action-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <Card className="card-hover h-full cursor-pointer group">
      <CardContent className="p-6">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${gradient}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-semibold text-lg mb-1 font-[Manrope] group-hover:text-indigo-600 transition-colors">{title}</h3>
        <p className="text-sm text-slate-500">{description}</p>
        <div className="mt-4 flex items-center text-indigo-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Comenzar <ArrowRight className="w-4 h-4 ml-1" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

export const Dashboard = () => {
  const { credits } = useCredits();
  const [stats, setStats] = useState({
    templates: 0,
    sectors: 0,
    campaigns: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templatesRes, sectorsRes, campaignsRes] = await Promise.all([
          templatesApi.list(),
          sectorsApi.list(),
          mauticApi.getCampaigns(),
        ]);
        
        setStats({
          templates: templatesRes.data.total,
          sectors: sectorsRes.data.sectors.length,
          campaigns: campaignsRes.data.campaigns,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const totalSent = stats.campaigns.reduce((acc, c) => acc + (c.sent || 0), 0);
  const totalOpened = stats.campaigns.reduce((acc, c) => acc + (c.opened || 0), 0);
  const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0;

  return (
    <div className="p-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Bienvenido a MarketFlow v2</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard 
          title="Créditos IA" 
          value={credits.balance} 
          icon={Sparkles}
          color="bg-gradient-to-br from-indigo-500 to-purple-600"
        />
        <MetricCard 
          title="Plantillas" 
          value={stats.templates}
          icon={LayoutTemplate}
          color="bg-gradient-to-br from-teal-500 to-emerald-600"
        />
        <MetricCard 
          title="Emails Enviados" 
          value={totalSent.toLocaleString()}
          icon={Mail}
          change={12}
          color="bg-gradient-to-br from-orange-500 to-amber-600"
        />
        <MetricCard 
          title="Tasa Apertura" 
          value={`${openRate}%`}
          icon={MousePointerClick}
          change={5}
          color="bg-gradient-to-br from-pink-500 to-rose-600"
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold font-[Manrope] mb-4">Acciones rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickAction
            title="Nueva Campaña"
            description="Crea y envía una nueva campaña de email marketing"
            icon={Megaphone}
            href="/campaigns/new"
            gradient="bg-gradient-to-br from-indigo-500 to-purple-600"
          />
          <QuickAction
            title="Generar Plantilla"
            description="Crea una plantilla personalizada con IA"
            icon={Sparkles}
            href="/templates/generate"
            gradient="bg-gradient-to-br from-pink-500 to-rose-600"
          />
          <QuickAction
            title="Gestionar Sectores"
            description="Administra los sectores e industrias"
            icon={Layers}
            href="/sectors"
            gradient="bg-gradient-to-br from-teal-500 to-emerald-600"
          />
        </div>
      </div>

      {/* Recent Campaigns */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold font-[Manrope]">Campañas recientes</h2>
          <Link to="/campaigns">
            <Button variant="ghost" size="sm" data-testid="view-all-campaigns">
              Ver todas <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : stats.campaigns.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {stats.campaigns.slice(0, 5).map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        campaign.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                      }`}>
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{campaign.name}</p>
                        <p className="text-sm text-slate-500">{campaign.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{campaign.sent}</p>
                        <p className="text-slate-500">Enviados</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{campaign.opened}</p>
                        <p className="text-slate-500">Abiertos</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{campaign.clicked}</p>
                        <p className="text-slate-500">Clicks</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay campañas recientes</p>
                <Link to="/campaigns/new">
                  <Button className="mt-4" variant="outline" size="sm" data-testid="create-first-campaign">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear primera campaña
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
